import React from 'react';
import { BookUser, Clock, Eye, FileText, FileUp, LayoutTemplate, Library, Loader2, PenLine, Sparkles, Trash2, FileLock2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import type { ThemeColors } from '../../../contexts/ThemeContext';

const RAIL_ICON = { size: 22, strokeWidth: 1.75 } as const;

interface RailButtonProps {
  label: string;
  title: string;
  active?: boolean;
  disabled?: boolean;
  danger?: boolean;
  iconClassName?: string;
  onClick?: () => void;
  onMouseDown?: () => void;
  children: React.ReactNode;
  buttonRef?: React.RefObject<HTMLButtonElement | null>;
  ariaExpanded?: boolean;
  ariaHaspopup?: boolean;
}

function RailButton({
  label,
  title,
  active,
  disabled,
  danger,
  iconClassName,
  onClick,
  onMouseDown,
  children,
  buttonRef,
  ariaExpanded,
  ariaHaspopup,
}: RailButtonProps) {
  return (
    <button
      ref={buttonRef as React.Ref<HTMLButtonElement>}
      type="button"
      onClick={onClick ?? (() => undefined)}
      onMouseDown={onMouseDown}
      disabled={disabled}
      title={title}
      aria-label={label}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHaspopup}
      className={cn(
        'group/rail-btn relative flex items-center gap-2.5 w-[calc(100%-0.5rem)] mx-1 px-2 py-2 rounded-lg text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
        active
          ? 'bg-indigo-500/10 dark:bg-indigo-500/15 border-l-2 border-indigo-500 pl-1.5'
          : danger
            ? 'hover:bg-red-500/10 dark:hover:bg-red-500/15'
            : 'hover:bg-gray-200/80 dark:hover:bg-gray-800/90',
      )}
    >
      <span
        className={cn(
          'flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-lg transition-colors',
          active
            ? 'bg-indigo-500/15 dark:bg-indigo-500/20'
            : danger
              ? 'bg-transparent'
              : 'bg-gray-100/80 dark:bg-gray-800/80 group-hover/rail-btn:bg-gray-200 dark:group-hover/rail-btn:bg-gray-700/80',
          danger ? 'text-red-600 dark:text-red-400' : iconClassName,
        )}
      >
        {children}
      </span>
      <span
        className={cn(
          'text-sm font-medium truncate overflow-hidden transition-all duration-200',
          'w-0 opacity-0 max-lg:group-hover/rail:w-auto max-lg:group-hover/rail:opacity-100',
          'lg:w-auto lg:opacity-100',
          active
            ? 'text-indigo-700 dark:text-indigo-300'
            : danger
              ? 'text-red-600 dark:text-red-400'
              : 'text-gray-700 dark:text-gray-300',
        )}
      >
        {label}
      </span>
    </button>
  );
}

interface RailSectionProps {
  title: string;
  colors: ThemeColors;
  showMobileDivider?: boolean;
  compactTop?: boolean;
  children: React.ReactNode;
}

function RailSection({
  title,
  colors,
  showMobileDivider = false,
  compactTop = false,
  children,
}: RailSectionProps) {
  return (
    <div role="group" aria-label={title}>
      {showMobileDivider && (
        <div className={cn('my-1 mx-2 border-t lg:hidden', colors.border)} aria-hidden />
      )}
      <p
        className={cn(
          'hidden lg:block px-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wider truncate',
          compactTop ? 'pt-0' : 'pt-2',
          'text-gray-500 dark:text-gray-400',
        )}
      >
        {title}
      </p>
      <div className="flex flex-col gap-0.5">{children}</div>
    </div>
  );
}

export interface ComposeToolRailProps {
  colors: ThemeColors;
  isImportingMessage: boolean;
  libraryOpen: boolean;
  addressBookOpen: boolean;
  aiPanelOpen?: boolean;
  aiEmailsEnabled?: boolean;
  signaturesPanelOpen?: boolean;
  fileSendPanelOpen?: boolean;
  savingLibraryTemplate: boolean;
  saveDraftDisabled?: boolean;
  saveAsTemplateTitle?: string;
  onImportClick: () => void;
  onLibrary: () => void;
  onFileSendLink: () => void;
  onToggleAddressBook: () => void;
  onToggleSignatures: () => void;
  onSaveAsDraft: () => void;
  onSaveAsTemplate: () => void;
  onPreview: () => void;
  onAiMouseDown: () => void;
  onAi: () => void;
  onDiscard: () => void;
  canScheduleSend?: boolean;
  onSchedule?: () => void;
  scheduleDisabled?: boolean;
}

const ComposeToolRail: React.FC<ComposeToolRailProps> = ({
  colors,
  isImportingMessage,
  libraryOpen,
  addressBookOpen,
  aiPanelOpen = false,
  aiEmailsEnabled = true,
  signaturesPanelOpen = false,
  fileSendPanelOpen = false,
  savingLibraryTemplate,
  saveDraftDisabled = false,
  saveAsTemplateTitle = 'Save message as a library template',
  onImportClick,
  onLibrary,
  onFileSendLink,
  onToggleAddressBook,
  onToggleSignatures,
  onSaveAsDraft,
  onSaveAsTemplate,
  onPreview,
  onAiMouseDown,
  onAi,
  onDiscard,
  canScheduleSend = false,
  onSchedule,
  scheduleDisabled = false,
}) => {
  return (
    <aside
      className={cn(
        'group/rail flex-shrink-0 flex flex-col self-stretch min-h-0 border-r py-2',
        'w-12 max-lg:group-hover/rail:w-52 lg:w-52',
        'transition-[width] duration-200 overflow-hidden',
        'border-gray-200 bg-gray-50',
        'dark:border-gray-800 dark:bg-gradient-to-b dark:from-gray-950 dark:to-gray-900',
      )}
      aria-label="Compose tools"
    >
      <div className="flex flex-col flex-1 min-h-0 overflow-x-hidden overflow-y-auto">
        <RailSection title="Insert" colors={colors} compactTop>
          <RailButton
            label="Library"
            title="Open a template from the library"
            active={libraryOpen}
            iconClassName="text-indigo-600 dark:text-indigo-400"
            onClick={onLibrary}
          >
            <Library {...RAIL_ICON} aria-hidden />
          </RailButton>

          <RailButton
            label="Import"
            title="Open message from .eml or .msg file"
            disabled={isImportingMessage}
            iconClassName="text-sky-600 dark:text-sky-400"
            onClick={onImportClick}
          >
            {isImportingMessage ? (
              <Loader2 className="w-[22px] h-[22px] animate-spin text-sky-600 dark:text-sky-400" strokeWidth={1.75} aria-hidden />
            ) : (
              <FileUp {...RAIL_ICON} aria-hidden />
            )}
          </RailButton>

          <RailButton
            label="Signature"
            title={signaturesPanelOpen ? 'Hide signatures' : 'Choose a signature'}
            active={signaturesPanelOpen}
            iconClassName="text-amber-600 dark:text-amber-400"
            onClick={onToggleSignatures}
          >
            <PenLine {...RAIL_ICON} aria-hidden />
          </RailButton>

          <RailButton
            label="Secure File"
            title={fileSendPanelOpen ? 'Hide secure file links' : 'Insert a secure file link'}
            active={fileSendPanelOpen}
            iconClassName="text-purple-600 dark:text-purple-400"
            onClick={onFileSendLink}
          >
            <FileLock2 {...RAIL_ICON} aria-hidden />
          </RailButton>
        </RailSection>

        <RailSection title="People" colors={colors} showMobileDivider>
          <RailButton
            label="Contacts"
            title={addressBookOpen ? 'Hide address book' : 'Address book (clients & collected)'}
            active={addressBookOpen}
            iconClassName="text-emerald-600 dark:text-emerald-400"
            onClick={onToggleAddressBook}
          >
            <BookUser {...RAIL_ICON} aria-hidden />
          </RailButton>
        </RailSection>

        <RailSection title="Review" colors={colors} showMobileDivider>
          <RailButton
            label="Preview"
            title="Preview message"
            iconClassName="text-slate-600 dark:text-slate-400"
            onClick={onPreview}
          >
            <Eye {...RAIL_ICON} aria-hidden />
          </RailButton>

          <RailButton
            label="AI Assistant"
            title={
              aiEmailsEnabled
                ? 'AI Assistant (rewrite, proofread, reply, etc.)'
                : 'AI email assistance is disabled in application settings'
            }
            active={aiPanelOpen}
            disabled={!aiEmailsEnabled}
            iconClassName="text-violet-600 dark:text-violet-400"
            onMouseDown={aiEmailsEnabled ? onAiMouseDown : undefined}
            onClick={aiEmailsEnabled ? onAi : undefined}
          >
            <Sparkles {...RAIL_ICON} aria-hidden />
          </RailButton>
        </RailSection>

        <RailSection title="Send" colors={colors} showMobileDivider>
          {canScheduleSend && onSchedule && (
            <RailButton
              label="Schedule"
              title="Schedule send"
              disabled={scheduleDisabled}
              iconClassName="text-blue-600 dark:text-blue-400"
              onClick={onSchedule}
            >
              <Clock {...RAIL_ICON} aria-hidden />
            </RailButton>
          )}

          <RailButton
            label="Save as Draft"
            title="Save as draft"
            disabled={saveDraftDisabled}
            iconClassName="text-teal-600 dark:text-teal-400"
            onClick={onSaveAsDraft}
          >
            <FileText {...RAIL_ICON} aria-hidden />
          </RailButton>

          <RailButton
            label="Save as Template"
            title={saveAsTemplateTitle}
            disabled={savingLibraryTemplate}
            iconClassName="text-cyan-600 dark:text-cyan-400"
            onClick={onSaveAsTemplate}
          >
            {savingLibraryTemplate ? (
              <Loader2 className="w-[22px] h-[22px] animate-spin text-cyan-600 dark:text-cyan-400" strokeWidth={1.75} aria-hidden />
            ) : (
              <LayoutTemplate {...RAIL_ICON} aria-hidden />
            )}
          </RailButton>
        </RailSection>
      </div>

      <div className="flex-shrink-0 pt-1">
        <div className="mb-1 mx-2 border-t border-gray-200 dark:border-gray-800" aria-hidden />
        <RailButton label="Discard" title="Discard message" danger onClick={onDiscard}>
          <Trash2 {...RAIL_ICON} aria-hidden />
        </RailButton>
      </div>
    </aside>
  );
};

export default ComposeToolRail;
